import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Backdrop, Box, Button, Card, Chip, CircularProgress, Container, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ArrowBack, Edit, Lock, ReceiptLong, Savings } from "@mui/icons-material";
import { getData } from "../../services/base.service";
import { useNotification } from "../Shared/NotificationProvider";
import { ProjectDetail } from "../../types/projectTypes";
import { panelSx, statusColor } from "./projectUtils";
import ProjectBillingTab from "./ProjectBillingTab";
import ProjectMarginTab from "./ProjectMarginTab";

// Full-screen "Billing & margin" page of a project: GIZ cost plan / billing and the internal margin calculation.
function ProjectControllingView() {
    const theme = useTheme();
    const navigate = useNavigate();
    const notify = useNotification();
    const { projectId } = useParams<{ projectId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const id = Number(projectId);
    const tab = searchParams.get('tab') === 'margin' ? 1 : 0;
    const [detail, setDetail] = useState<ProjectDetail | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [loading, setLoadingState] = useState(false);

    useEffect(() => {
        if (!id) return;
        getData(`projects/detail/${id}`).then((res) => setDetail(res as ProjectDetail))
            .catch((error) => notify(`Couldn't load the project: ${(error as Error).message}`, 'error'));
    }, [id, notify]);

    const setLoading = (value: boolean) => setLoadingState(value);

    const toggleEdit = () => {
        setIsEditMode(!isEditMode);
        notify(isEditMode ? 'View mode.' : 'Edit mode - changes are saved as you go.', 'info');
    };

    return (
        <Box sx={{ backgroundColor: theme.palette.grey[100], minHeight: 'calc(100vh - 64px)', paddingY: 2 }}>
            <Container maxWidth={false} sx={{ maxWidth: 1900 }}>
                <Card elevation={0} sx={{ ...panelSx(theme), marginBottom: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} gap={2} sx={{ padding: 2 }}>
                        <Button startIcon={<ArrowBack />} onClick={() => navigate(`/projects/${id}`)}>Project</Button>
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                            {detail ? (
                                <>
                                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
                                        {detail.project.project_number} - {detail.project.project_name}
                                    </Typography>
                                    <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
                                        <Chip size="small" label={detail.project.status || 'No status'} color={statusColor(detail.project.status)} />
                                        <Typography variant="body2" color="text.secondary">
                                            {[detail.figures?.client, detail.project.client_contract_number ? `VN ${detail.project.client_contract_number}` : null,
                                                detail.project.project_donor_number ? `PN ${detail.project.project_donor_number}` : null].filter(Boolean).join(' · ')}
                                        </Typography>
                                    </Stack>
                                </>
                            ) : <CircularProgress size={22} />}
                        </Box>
                        <Button variant={isEditMode ? 'contained' : 'outlined'} disableElevation startIcon={isEditMode ? <Lock /> : <Edit />} onClick={toggleEdit}>
                            {isEditMode ? 'Lock' : 'Edit'}
                        </Button>
                    </Stack>
                    <Tabs value={tab} onChange={(_, value) => setSearchParams(value === 1 ? { tab: 'margin' } : {})} sx={{ paddingX: 2 }}>
                        <Tab icon={<ReceiptLong fontSize="small" />} iconPosition="start" label="Billing" />
                        <Tab icon={<Savings fontSize="small" />} iconPosition="start" label="Margin calculation" />
                    </Tabs>
                </Card>

                <Card elevation={0} sx={{ ...panelSx(theme), padding: { xs: 1.5, md: 2.5 } }}>
                    {tab === 0
                        ? <ProjectBillingTab projectId={id} isEditMode={isEditMode} setLoading={setLoading} />
                        : <ProjectMarginTab projectId={id} isEditMode={isEditMode} setLoading={setLoading} />}
                </Card>
            </Container>
            <Backdrop open={loading} sx={{ zIndex: 9999, color: '#fff' }}><CircularProgress color="inherit" /></Backdrop>
        </Box>
    );
}

export default ProjectControllingView;
